/**
 * Validator module for validating game entries and user input
 * ES Module version for web UI
 * Requirements: 3.10, 5.1, 5.2, 5.3, 6.6
 */

import { GameStatus } from '../../models/GameEntry.js';

/**
 * Validates a Steam ID
 * @param {string} steamID - The Steam ID to validate
 * @returns {boolean} - True if valid, false otherwise
 * Requirements: 5.1
 */
export function validateSteamID(steamID) {
    return typeof steamID === 'string' && steamID.trim().length > 0;
}

/**
 * Validates a game title
 * @param {string} title - The title to validate
 * @returns {boolean} - True if valid, false otherwise
 * Requirements: 5.2
 */
export function validateTitle(title) {
    return typeof title === 'string' && title.trim().length > 0;
}

/**
 * Validates a URL format
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if valid, false otherwise
 * Requirements: 5.3
 */
export function validateURL(url) {
    if (typeof url !== 'string' || url.trim().length === 0) {
        return false;
    }

    try {
        new URL(url);
        return true;
    } catch (error) {
        return false;
    }
}

/**
 * Validates a rating value
 * @param {number} rating - The rating to validate
 * @returns {boolean} - True if valid (1-5 inclusive), false otherwise
 * Requirements: 6.6
 */
export function validateRating(rating) {
    return Number.isInteger(rating) && rating >= 1 && rating <= 5;
}

/**
 * Validates a game status value
 * @param {string} status - The status to validate
 * @returns {boolean} - True if valid GameStatus value, false otherwise
 * Requirements: 6.4
 */
export function validateStatus(status) {
    const validStatuses = Object.values(GameStatus);
    return validStatuses.includes(status);
}

/**
 * Validates a complete game entry
 * @param {Object} entry - The game entry to validate
 * @returns {Object} - Validation result with isValid boolean and errors array
 * Requirements: 3.10
 */
export function validateGameEntry(entry) {
    const errors = [];

    // Validate required fields
    if (!validateSteamID(entry.steamID)) {
        errors.push('Invalid or missing Steam ID');
    }

    if (!validateTitle(entry.title)) {
        errors.push('Invalid or missing title');
    }

    if (!validateURL(entry.storeURL)) {
        errors.push('Invalid or missing store URL');
    }

    if (!validateURL(entry.imageURL)) {
        errors.push('Invalid or missing image URL');
    }

    if (!Array.isArray(entry.genres)) {
        errors.push('Genres must be an array');
    }

    if (typeof entry.creator !== 'string') {
        errors.push('Creator must be a string');
    }

    const rd = entry.releaseDate instanceof Date ? entry.releaseDate : new Date(entry.releaseDate);
    if (!entry.releaseDate || isNaN(rd.getTime())) {
        errors.push('Invalid or missing release date');
    }

    if (typeof entry.isDLC !== 'boolean') {
        errors.push('isDLC must be a boolean');
    }

    // Validate optional user metadata fields if present
    if (entry.rating != null && entry.rating !== 0) {
        if (!validateRating(entry.rating)) {
            errors.push('Rating must be an integer between 1 and 5');
        }
    }

    if (entry.status != null && entry.status !== '') {
        if (!validateStatus(entry.status)) {
            errors.push('Invalid status value');
        }
    }

    if (entry.lastPlayed !== null && entry.lastPlayed !== undefined) {
        const dc = entry.lastPlayed instanceof Date ? entry.lastPlayed : new Date(entry.lastPlayed);
        if (isNaN(dc.getTime())) {
            errors.push('Invalid last played date');
        }
    }

    return {
        isValid: errors.length === 0,
        errors
    };
}

/**
 * Validates user metadata
 * @param {Object} metadata - The user metadata to validate
 * @returns {Object} - Validation result with isValid boolean and errors array
 * Requirements: 6.1, 6.2, 6.3, 6.5, 6.6
 */
export function validateUserMetadata(metadata) {
    const errors = [];

    for (const [key, value] of Object.entries(metadata)) {
        const field = key;

        if (field === 'rating' && value != null && value !== 0) {
            if (!validateRating(value)) {
                errors.push(`Rating must be an integer between 1 and 5 (${key})`);
            }
        }

        if (field === 'status' && value != null && value !== '') {
            if (!validateStatus(value)) {
                errors.push(`Invalid status value (${key})`);
            }
        }

        if (field === 'lastPlayed' && value !== null && value !== undefined) {
            const d = value instanceof Date ? value : new Date(value);
            if (isNaN(d.getTime())) {
                errors.push(`Invalid date (${key})`);
            }
        }
    }

    return {
        isValid: errors.length === 0,
        errors
    };
}
